import { useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react'

type Options<T> = {
  /** Serialize before writing to storage. Defaults to JSON.stringify. */
  serialize?: (value: T) => string
  /** Parse the stored string back into state. Defaults to JSON.parse. */
  deserialize?: (raw: string) => T
}

/** Keys that hold dashboard UI state (filters, etc.) in sessionStorage. */
const DASHBOARD_STORAGE_PREFIXES = ['bd.', 'ca.'] as const

function currentUserId(): string | null {
  try {
    return localStorage.getItem('user_id')
  } catch {
    return null
  }
}

/** Scope a storage key to the logged-in user so accounts never share filters. */
export function scopedDashboardKey(key: string): string {
  const userId = currentUserId()
  return userId ? `${userId}:${key}` : key
}

function matchesDashboardKey(storageKey: string): boolean {
  return DASHBOARD_STORAGE_PREFIXES.some(
    (prefix) =>
      storageKey === prefix ||
      storageKey.startsWith(prefix) ||
      storageKey.includes(`:${prefix}`),
  )
}

/**
 * Remove business / collection analytics UI persistence.
 * Called on login + logout so filters never leak across users or re-logins
 * in the same browser tab (sessionStorage survives SPA auth changes).
 */
export function clearDashboardPersistence(): void {
  try {
    const toRemove: string[] = []
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i)
      if (key && matchesDashboardKey(key)) toRemove.push(key)
    }
    toRemove.forEach((key) => sessionStorage.removeItem(key))
  } catch {
    /* storage unavailable */
  }

  try {
    localStorage.removeItem('business_dashboard_target_excel_v1')
  } catch {
    /* storage unavailable */
  }
}

/**
 * useState backed by sessionStorage so the value survives component
 * unmount/remount (e.g. switching dashboard tabs) and accidental reloads
 * within the same browser-tab session for the *same* user.
 *
 * Keys are namespaced by `user_id`. Cleared on login/logout via
 * {@link clearDashboardPersistence}.
 *
 * `serialize`/`deserialize` let non-JSON values (e.g. Set) round-trip.
 */
export function usePersistentState<T>(
  key: string,
  initial: T | (() => T),
  options: Options<T> = {},
): [T, Dispatch<SetStateAction<T>>] {
  const deserialize = options.deserialize ?? ((raw: string) => JSON.parse(raw) as T)
  const serialize = options.serialize ?? ((value: T) => JSON.stringify(value))
  const serializeRef = useRef(serialize)
  serializeRef.current = serialize

  const storageKey = scopedDashboardKey(key)

  const [state, setState] = useState<T>(() => {
    try {
      // Drop legacy unscoped keys so an old shared value cannot resurrect.
      if (storageKey !== key) {
        window.sessionStorage.removeItem(key)
      }
      const raw = window.sessionStorage.getItem(storageKey)
      if (raw !== null) return deserialize(raw)
    } catch {
      /* storage unavailable or corrupt — fall back to the initial value */
    }
    return typeof initial === 'function' ? (initial as () => T)() : initial
  })

  useEffect(() => {
    try {
      window.sessionStorage.setItem(storageKey, serializeRef.current(state))
    } catch {
      /* quota exceeded or storage unavailable — ignore, keep in-memory state */
    }
  }, [storageKey, state])

  return [state, setState]
}
